import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { endOfModuleSurveySelected, requestReviewStudentResponses, submitResponse } from '../redux/actions/ui';
import { ConfettiComp } from '../components/confetti-comp';
import '../css/module-completion.css';
import { ModuleCompletionContent } from '../page-content/module-completion-content';
import {
  ITSEventType,
  ModuleCompletionData,
  StudentProblemDisplay,
  CompetencyDialog,
} from 'its-pages';
import { Box, Button, SxProps } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { generateEndOfModuleSurveyLink } from '../utils/survey';

type Props = {
  moduleCompletionContent: ModuleCompletionContent;
}

export const ModuleCompletion = (props: Props) => {
  // TODO: class code and user id should be passed in from the client

  const { moduleCompletionContent } = props;
  const pageData = moduleCompletionContent.pageData as ModuleCompletionData;
  const { currentSkill } = pageData;

  const [currentProblem, setCurrentProblem] = useState(0);
  const userId = useAppSelector(state => state.ui.userId);
  const classId = useAppSelector(state => state.ui.classId);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const timeout = setTimeout(() => dispatch(submitResponse()), 100);
    return () => clearTimeout(timeout);
  }, [dispatch]);

  const [showCompetencyDescription, setShowCompetencyDescription] =
    useState<boolean>(false);
  const onShowCompetencyDescriptionClick = () => {
    setShowCompetencyDescription(true);
  };

  const [showResults, setShowResults] = useState<boolean>(false);
  enum Direction {
    prev = -1,
    next = 1
  }
  const onPrevNext = (direction: Direction) => () => {
    setCurrentProblem(currentProblem + direction);
    pageData.recordEvent(ITSEventType.postCompletionProblemReview);
  };

  const gridContainer: SxProps = {
    display: 'grid',
    width: '100%',
    maxWidth: '640px',
    gridTemplateAreas: `
    "description" 
    "review-results"
    "review-description"`,
    gap: 3
  };

  const skillSurveyLink = generateEndOfModuleSurveyLink(
    currentSkill.name, classId, userId || Date.now().toString());
  const onSurveyClick = useCallback(() => {
    dispatch(endOfModuleSurveySelected());
    if (skillSurveyLink)
      window.open(skillSurveyLink, '_blank');
  }, [dispatch, skillSurveyLink]);

  const onReviewResponsesClick = useCallback(() => {
    dispatch(requestReviewStudentResponses());
  }, [dispatch])

  return (
    <div className='Problem ModuleCompletion'>
      <ConfettiComp numberOfPieces={100} gravity={0.1} />
      <Box sx={gridContainer}>
        <Box sx={{ gridArea: 'description' }}>
          <div className='CurrentSkillTitle'>
            {currentSkill.name.toLocaleUpperCase()}
          </div>
          <div className='AllDone'>Skill Completed!</div>
          <div className='GreatJob'>
            Great Job going through the {currentSkill.name.toLocaleLowerCase()}{' '}
            skill. With enough practice you{"'"}ll be better prepared to help
            patients.
          </div>
        </Box>
        {skillSurveyLink && (
          <Button onClick={onSurveyClick}  variant="contained" color="primary">
            Please complete the brief survey to finish the assignment.
          </Button>
        )}
        <Button
          variant="text"
          size="small"
          onClick={onShowCompetencyDescriptionClick}
        >
          Review Skill Description
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={onReviewResponsesClick}
        >
          Review Responses and Additional Practice
        </Button>
      </Box>
      <CompetencyDialog
        open={showCompetencyDescription}
        competencyDescription={pageData.competencyText}
        onClose={() => setShowCompetencyDescription(false)}
      />
      <StudentProblemDisplay
        open={showResults}
        onClose={() => setShowResults(false)}
        onPrev={currentProblem ? onPrevNext(Direction.prev) : undefined}
      ></StudentProblemDisplay>
    </div>
  );
};

export default ModuleCompletion;
